import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { Swiper } from "swiper/react";
import type { SwiperProps } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper";
import { Navigation, Pagination, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface BaseCarouselProps extends Omit<SwiperProps, "modules"> {
  children: ReactNode;
  className?: string;
  autoUpdateDelay?: number;
}

export function BaseCarousel({
  children,
  className = "",
  autoUpdateDelay = 100,
  ...swiperProps
}: BaseCarouselProps) {
  const swiperRef = useRef<SwiperClass | null>(null);

  useEffect(() => {
    if (swiperRef.current) {
      const timeout = setTimeout(() => {
        swiperRef.current?.update();
      }, autoUpdateDelay);

      return () => clearTimeout(timeout);
    }

    return () => undefined;
  }, [children, autoUpdateDelay]);

  return (
    <Swiper
      modules={[Navigation, Pagination, Autoplay]}
      className={className}
      onSwiper={(swiper) => {
        swiperRef.current = swiper;
      }}
      {...swiperProps}
    >
      {children}
    </Swiper>
  );
}
